import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { API } from 'api';

import { SECTIONS, DOMAINS, IMPORTANCE, INTEGRATIONS } from '@repo/monitoring';

import { apiName, apiBasePath } from 'assets';
import { getMainAccountId } from 'store/account';
import { Subscription, SubscriptionStatus } from 'types/Subscription';
import { parseDate } from '@repo/utils';
import { MembershipPlan } from 'types/MainAccount';

export const ShopifyProductType = {
  ANNUAL_MEMBERSHIP: 'annual_membership',
  BASELINE_ASSESSMENT: 'baseline_assessment',
  INTENSIVE_PROGRAM: 'intensive_program',
};

type SubscriptionType = 'tiny_plus' | 'practitioner' | 'kits';

export async function getSubscriptions(type?: SubscriptionType) {
  const params = type ? `?type=${type}` : '';
  const response = await API.get(
    apiName,
    `${apiBasePath}/subscriptions${params}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.ACCOUNT,
        domain: DOMAINS.SUBSCRIPTIONS,
        importance: IMPORTANCE.HIGH,
        integration: INTEGRATIONS.STRIPE,
      },
      context: {
        subscriptionType: type,
      },
    },
  );
  return response?.result;
}

interface SubscriptionLists {
  all: Subscription[];
  active: Subscription[];
  inactive: Subscription[];
}

interface UseSubscriptionsResult {
  data: Subscription[] | undefined;
  lists: SubscriptionLists;
  isTinyPlus: boolean;
  isTinyPlusPrograms: boolean;
  isTinyPlusProactive: boolean;
  isAnyTinyPlus: boolean;
  [key: string]: unknown;
}

const useSubscriptions = (type?: SubscriptionType): UseSubscriptionsResult => {
  const mainAccountId = useSelector(getMainAccountId);
  const subscriptionsListQuery = useQuery<
    Subscription[],
    Error,
    Subscription[]
  >({
    queryKey: [apiName, `subscriptionsList`, mainAccountId, type],
    queryFn: () => getSubscriptions(type),
    select: subscriptions => subscriptions || [],
    enabled: !!mainAccountId,
  });

  const { data: subscriptions, ...rest } = subscriptionsListQuery;

  const all = subscriptions || [];
  const active = all
    .filter(obj => obj.status === SubscriptionStatus.ACTIVE)
    .sort((a, b) => {
      return (
        parseDate(b.date_created_at).getTime() -
        parseDate(a.date_created_at).getTime()
      );
    });

  const inactive = all
    .filter(obj => obj.status !== SubscriptionStatus.ACTIVE)
    .sort((a, b) => {
      return (
        parseDate(b.date_updated_at).getTime() -
        parseDate(a.date_updated_at).getTime()
      );
    });

  let membershipPlan = null;
  if (active.length > 0) {
    const mainMembership = active[0];
    membershipPlan = MembershipPlan.TINY_PLUS;
    if (
      mainMembership.shopify_product?.type ===
      ShopifyProductType.ANNUAL_MEMBERSHIP
    ) {
      membershipPlan = MembershipPlan.TINY_PLUS_PROACTIVE;
    } else if (
      mainMembership.shopify_product?.type ===
      ShopifyProductType.INTENSIVE_PROGRAM
    ) {
      membershipPlan = MembershipPlan.TINY_PLUS_PROGRAMS;
    }
  }

  return {
    ...rest,
    data: subscriptions,
    lists: { all, active, inactive },
    isTinyPlus: membershipPlan === MembershipPlan.TINY_PLUS,
    isTinyPlusPrograms: membershipPlan === MembershipPlan.TINY_PLUS_PROGRAMS,
    isTinyPlusProactive: membershipPlan === MembershipPlan.TINY_PLUS_PROACTIVE,
    isAnyTinyPlus:
      !!membershipPlan &&
      [
        MembershipPlan.TINY_PLUS,
        MembershipPlan.TINY_PLUS_PROGRAMS,
        MembershipPlan.TINY_PLUS_PROACTIVE,
      ].includes(membershipPlan),
  };
};

export default useSubscriptions;
